import { useAppStore } from '../../state/appStore';
import { useProgressStore } from '../../state/progressStore';
import { getLevel, orderedLevels } from '../../levels';
import { useT } from '../hooks';
import { BigButton, Panel, ScreenShell, Stars } from '../components';

function Metric({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <div className="flex flex-col items-center rounded-xl bg-white/5 px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
      <div className={`text-2xl font-bold ${good ? 'text-emerald-300' : 'text-rose-300'}`}>
        {value}
      </div>
    </div>
  );
}

export function Debrief() {
  const t = useT();
  const { debrief, activeLevelId, openIntro, restartLevel, exitToMenu } = useAppStore();
  const progress = useProgressStore((s) => s.progress);
  if (!debrief || !activeLevelId) return null;
  const { result, outcome } = debrief;
  const level = getLevel(activeLevelId);
  const m = result.metrics;

  const next = orderedLevels(level.track).find(
    (l) => !l.freeRoam && l.order > level.order && progress.unlockedLevels.includes(l.id),
  );
  const isManual = level.track === 'manual';

  return (
    <ScreenShell>
      <div className="text-center">
        <div className="text-5xl">{result.passed ? '🎉' : '🔁'}</div>
        <h2 className={`mt-2 text-4xl font-black ${result.passed ? 'text-emerald-300' : 'text-rose-300'}`}>
          {result.passed ? t('debrief.passed') : t('debrief.failed')}
        </h2>
        <div className="mt-2 flex items-center justify-center gap-4">
          <Stars n={m.stars} />
          <span className="text-2xl font-bold text-white/90">
            {t('debrief.score')}: {m.score}/100
          </span>
          {outcome.xpEarned > 0 && (
            <span className="rounded-full bg-amber-400/20 px-3 py-1 text-sm font-semibold text-amber-300">
              +{outcome.xpEarned} {t('debrief.xp')}
            </span>
          )}
        </div>
      </div>

      {isManual && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label={t('debrief.stalls')} value={`${m.stalls}`} good={m.stalls === 0} />
          <Metric label={t('debrief.clutch')} value={`${m.clutchScore}`} good={m.clutchScore >= 70} />
          <Metric label={t('debrief.gears')} value={`${m.gearScore}`} good={m.gearScore >= 70} />
          <Metric
            label={t('debrief.rollback')}
            value={`${m.rollbackM.toFixed(2)} m`}
            good={m.rollbackM <= 0.4}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Metric label={t('debrief.smoothness')} value={`${m.smoothness}`} good={m.smoothness >= 70} />
        <Metric label={t('debrief.observation')} value={`${m.observation}`} good={m.observation >= 70} />
        <Metric label={t('debrief.rules')} value={`${m.ruleCompliance}`} good={m.ruleCompliance >= 70} />
        <Metric
          label={t('debrief.speed')}
          value={`${Math.round(m.speedCompliance * 100)}%`}
          good={m.speedCompliance >= 0.8}
        />
        <Metric label={t('debrief.collisions')} value={`${m.collisions}`} good={m.collisions === 0} />
        <Metric label={t('debrief.time')} value={`${Math.round(m.timeS)}s`} good />
      </div>

      {result.failures.length > 0 && (
        <Panel className="px-5 py-4">
          <div className="mb-2 text-sm uppercase tracking-wider text-rose-300/80">
            {t('debrief.failures')}
          </div>
          <ul className="list-disc pl-5 text-white/80">
            {result.failures.map((f) => (
              <li key={f}>{t(f)}</li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel className="px-5 py-4">
        <div className="mb-2 text-sm uppercase tracking-wider text-sky-300/80">
          🧑‍🏫 {t('debrief.tips')}
        </div>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-white/80">
          {result.tips.map((tip) => (
            <li key={tip}>{t(tip)}</li>
          ))}
        </ul>
      </Panel>

      <div className="flex flex-wrap justify-center gap-3">
        <BigButton variant="ghost" onClick={exitToMenu}>
          {t('debrief.menu')}
        </BigButton>
        <BigButton variant={result.passed ? 'ghost' : 'primary'} onClick={restartLevel}>
          🔁 {t('debrief.retry')}
        </BigButton>
        {result.passed && next && (
          <BigButton onClick={() => openIntro(next.id)}>▶ {t('debrief.next')}</BigButton>
        )}
      </div>
    </ScreenShell>
  );
}
